// services/firebase.ts — Single initialisation of the modular
// Firebase SDK. Everything else in the app imports `auth`, `db`,
// and `functions` from this module.
//
// Config source: `/__/firebase/init.json` (Firebase Hosting reserved
// URL that serves the project's web config). Fetched at module-eval
// time via top-level await; Vite bundles this as part of the entry
// chunk, so the network roundtrip happens before any React render.
//
// authDomain override: same-origin so the Firebase Auth handler
// (/__/auth/handler) runs on the same origin as the console.
// Without this, iOS Safari's ITP + Chrome third-party cookie
// partitioning silently drops the auth-state postMessage across
// origins (e.g. signed-in on console.socialmesh.app, but Firebase
// would try social-mesh-app.firebaseapp.com).
//
// One-shot guarantee: increments `bootCounters.firebaseInit` exactly
// once. Re-imports return the same singleton instances.

import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  getAuth,
  setPersistence,
  browserLocalPersistence,
  type Auth,
} from 'firebase/auth';
import { getFirestore, type Firestore } from 'firebase/firestore';
import { getFunctions, type Functions } from 'firebase/functions';
import { bump } from '@/utils/bootCounters';

interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
}

async function loadConfig(): Promise<FirebaseConfig> {
  const res = await fetch('/__/firebase/init.json', {
    credentials: 'same-origin',
  });
  if (!res.ok) {
    throw new Error(
      `[firebase] /__/firebase/init.json returned ${res.status}; is this page being served from Firebase Hosting?`,
    );
  }
  return (await res.json()) as FirebaseConfig;
}

const rawConfig = await loadConfig();

const config: FirebaseConfig = {
  ...rawConfig,
  // Override authDomain to the current host so /__/auth/handler runs
  // same-origin. Falls back to the config value for non-browser test
  // contexts.
  authDomain:
    typeof window !== 'undefined'
      ? window.location.hostname
      : rawConfig.authDomain,
};

export const app: FirebaseApp = initializeApp(config);
export const auth: Auth = getAuth(app);
export const db: Firestore = getFirestore(app);
export const functions: Functions = getFunctions(app);

// Explicitly request LOCAL persistence. Default is already LOCAL on
// web, but stating it removes any ambiguity if a future SDK release
// flips defaults. Fire-and-forget: a failure here doesn't block
// boot, the auth listener will recover.
setPersistence(auth, browserLocalPersistence).catch(() => {});

// Suppress a documented Firebase SDK race condition (firebase-js-sdk#6796)
// where signInWithPopup's iframe fires an auth event before the
// promise is registered, producing an unhandled rejection. The
// legacy `firebase-init.js` also did this. Keeping parity so the
// console behaves identically across the marketing + React surfaces.
if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason as { message?: string } | undefined;
    const msg = reason?.message ?? String(event.reason ?? '');
    const suppressed =
      msg.includes('INTERNAL ASSERTION FAILED') ||
      msg.includes('Pending promise was never set');
    if (suppressed) event.preventDefault();
  });
}

bump('firebaseInit');
